// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue';
import Vuex from 'vuex';
import VuexI18n from 'vuex-i18n';

import App from './App.vue';
import './theme.scss';
import router from './router';

Vue.config.productionTip = false;
Vue.use(Vuex);

import createStore, { LightStatus, PairingStatus } from './store';
import {
  requestDeviceInfo,
  confirmLightCreation,
  sendCredentials,
} from './helpers/lightManager';
import {
  requestEncryptedSSIDAndPassword,
  createLightInCloud,
} from './helpers/apiManager';
import { setInterval } from 'timers';
import * as _ from 'lodash';
import WindowCloseReason from './enums/WindowCloseReason';
import DeviceType from './enums/DeviceType';

import { initSentry } from './helpers/sentryManager';
import { SENTRY_VERSION } from './sentry-version';

import translationsEn from './resources/i18n/en.json';
import translationsFr from './resources/i18n/fr.json';
import translationsDe from './resources/i18n/de.json';
import translationsEs from './resources/i18n/es.json';
import translationsIt from './resources/i18n/it.json';
import translationsNl from './resources/i18n/nl.json';
import translationsPl from './resources/i18n/pl.json';
import translationsRu from './resources/i18n/ru.json';
import translationsUk from './resources/i18n/uk.json';
import translationsZhCN from './resources/i18n/zh-rCN.json';
import translationsZhHant from './resources/i18n/zh-rHK.json';
import translationsZhHK from './resources/i18n/zh-rHK.json';
import translationsZhTW from './resources/i18n/zh-rTW.json';
import translationsId from './resources/i18n/id.json';
import translationsPtBR from './resources/i18n/pt-rBR.json';
import translationsPtPT from './resources/i18n/pt-rPT.json';
import translationsCs from './resources/i18n/cs.json';
import translationsTr from './resources/i18n/tr.json';
import translationsKo from './resources/i18n/ko.json';
import translationsJa from './resources/i18n/ja.json';

const store = createStore();

Vue.use(VuexI18n.plugin, store);
Vue.i18n.add('en', translationsEn);
Vue.i18n.add('fr', translationsFr);
Vue.i18n.add('de', translationsDe);
Vue.i18n.add('es', translationsEs);
Vue.i18n.add('it', translationsIt);
Vue.i18n.add('nl', translationsNl);
Vue.i18n.add('pl', translationsPl);
Vue.i18n.add('ru', translationsRu);
Vue.i18n.add('uk', translationsUk);
Vue.i18n.add('zh', translationsZhCN);
Vue.i18n.add('zh-HK', translationsZhHK);
Vue.i18n.add('zh-TW', translationsZhTW);
Vue.i18n.add('zh-Hant-TW', translationsZhTW);
Vue.i18n.add('zh-Hant-HK', translationsZhHK);
Vue.i18n.add('zh-Hant-MO', translationsZhHK);
Vue.i18n.add('zh-Hant', translationsZhHant);
Vue.i18n.add('id', translationsId);
Vue.i18n.add('pt-BR', translationsPtBR);
Vue.i18n.add('pt-PT', translationsPtPT);
Vue.i18n.add('cs', translationsCs);
Vue.i18n.add('tr', translationsTr);
Vue.i18n.add('ko', translationsKo);
Vue.i18n.add('ja', translationsJa);

const locale = navigator.language || 'en';
Vue.i18n.fallback('en');
Vue.i18n.set(locale);

store.commit('updatePairingStatus', {
  type: 'waitingForCredentials',
  ssidNotFound: false,
  passwordIsWrong: false,
  ssidList: [],
});
// Updating light status
setInterval(async () => {
  // don't request the light status if the light has already get the IP address
  // from the router. It will shutdown the internal IP and during this time
  // it doesn't expect any requests from the phone
  // Otherwise the firmware may crash
  if (store.state.lightStatus !== LightStatus.IPObtained) {
    try {
      const result = await requestDeviceInfo();
      console.log(`New light status ${JSON.stringify(result)}`);
      // if for whatever reason phone connects to another light's AP
      // –> discard the changes
      if (
        store.state.lightMacAddress == undefined ||
        store.state.lightMacAddress === (<any>result.data).mac
      ) {
        store.commit('updateLightStatus', <any>result.data);
      } else {
        console.log(
          `Wrong mac, skipping. Expected ${
          store.state.lightMacAddress
          }, received ${(<any>result.data).mac}`,
        );
      }
    } catch (e) { }
  }
}, 3000);

// Pairing status watcher
store.watch(
  () => store.getters.status,
  async ({
    pairingStatus,
    lightStatus,
    currentSSID,
  }: {
    pairingStatus: PairingStatus;
    lightStatus: LightStatus;
    currentSSID: string;
  }) => {
    console.log(
      `New status! ${JSON.stringify(pairingStatus)}, ${JSON.stringify(
        lightStatus,
      )}`,
    );
    // finishing pairing – nothing to do there
    if (pairingStatus.type === 'finishingPairing') {
      return;
    }
    // If we're ready to enter new password -> try to connect to the ssid
    if (
      pairingStatus.type === 'waitingForCredentials' &&
      pairingStatus.passwordIsWrong === true
    ) {
      await requestToConnectToMainAPIfNeeded();
      await waitForConnectionToTheSSID(store.state.registrationData!.ssid);
    }

    // encrypting creds
    if (pairingStatus.type === 'encryptingCredentials') {
      try {
        const dt = await requestEncryptedSSIDAndPassword(
          store.state.registrationData!.ssid,
          store.state.registrationData!.password,
          store.state.registrationData!.accessToken,
          store.state.registrationData!.env,
          store.state.registrationData!.region,
          store.state.registrationData!.thirdParty
        );
        store.commit('lightCredentialsUpdated', (<any>dt.data).data);
        store.commit('updatePairingStatus', { type: 'connectingToTheLight' });
        notifyAppReachedConnectingState();
        return;
      } catch {
        store.commit('updatePairingStatus', {
          type: 'encryptingCredentialsFail',
        });
      }
    }
    // connecting to the light
    if (
      pairingStatus.type === 'connectingToTheLight' &&
      (lightStatus === LightStatus.Idle ||
        lightStatus === LightStatus.WrongPassword ||
        lightStatus === LightStatus.APNotFound ||
        lightStatus === LightStatus.ConnectionFailed)
    ) {
      store.commit('updatePairingStatus', {
        type: 'sendingCredentials',
      });


      window.setTimeout(() => {
        if (
          store.state.pairingStatus.type ===
          'sendingCredentials' &&
          !store.state.pairingStatus.hintTimeoutReached
        ) {
          console.log('Timeout reached when sending credentials. Showing hint');
          store.commit('updatePairingStatus', {
            type: 'sendingCredentials',
            hintTimeoutReached: true
          });
        }
      }, 10000);

      console.log(
        `Light credentials are ${JSON.stringify(
          store.state.credentialsForTheLight,
        )}`,
      );
      await sendCredentials(store.state.credentialsForTheLight);
      store.commit('updateLightStatus', { status: LightStatus.Disconnected });
      store.commit('updatePairingStatus', {
        type: 'waitingDeviceToConnectToTheWiFi',
      });
      window.setTimeout(() => {
        console.log(
          "Checking the current SSID is indeed the light SSID if we're still trying to connect",
        );
        requestToConnectToLightAPIfNeeded();
      }, 15000);
      // case – lamp status is connected to WiFi, but the phone is not
      // connected to the light AP.
      // And the system didn't succeed in connecting the phone
      // to the light AP.
      // Show the connection hint this case
      window.setTimeout(() => {
        if (
          store.state.pairingStatus.type ===
          'waitingDeviceToConnectToTheWiFi' &&
          lightStatus !== LightStatus.WrongPassword &&
          lightStatus !== LightStatus.APNotFound &&
          lightStatus !== LightStatus.ConnectionFailed &&
          pairingAttempt === store.state.pairingAttempt &&
          !store.state.pairingStatus.hintTimeoutReached
        ) {
          console.log('Timeout reached when light was trying to connect to the WiFi, showing hint');
          store.commit('updatePairingStatus', {
            type: 'waitingDeviceToConnectToTheWiFi',
            hintTimeoutReached: true
          });
        }
      }, 25000);

      const pairingAttempt = store.state.pairingAttempt;
      // checking another case – lamp status is stuck in connecting to the wifi state
      // if nothing was changed in 45 secs and we're still in the same pairing attempt
      // -> show timeout error
      window.setTimeout(() => {
        console.log('Checking that status is changed');
        if (
          store.state.pairingStatus.type ===
          'waitingDeviceToConnectToTheWiFi' &&
          lightStatus !== LightStatus.WrongPassword &&
          lightStatus !== LightStatus.APNotFound &&
          lightStatus !== LightStatus.ConnectionFailed &&
          pairingAttempt === store.state.pairingAttempt
        ) {
          console.log('Timeout reached when trying to connect to the WiFi');
          store.commit('updatePairingStatus', {
            type: 'lightConnectionToWifiFail',
          });
        }
      }, 75000);
      return;
    }
    // light is trying to connect to the wifi
    if (
      lightStatus === LightStatus.Connecting &&
      pairingStatus.type !== 'waitingDeviceToConnectToTheWiFi'
    ) {
      store.commit('updatePairingStatus', {
        type: 'waitingDeviceToConnectToTheWiFi',
      });
      return;
    }
    // wrong password
    if (
      lightStatus === LightStatus.WrongPassword &&
      pairingStatus.type === 'waitingDeviceToConnectToTheWiFi'
    ) {
      console.log('Wrong pwd, cleaning creds');
      store.commit('cleanEncryptedCredentialsWrongPassword');
      return;
    }
    // No SSID
    if (
      (lightStatus === LightStatus.APNotFound ||
        lightStatus === LightStatus.ConnectionFailed) &&
      pairingStatus.type === 'waitingDeviceToConnectToTheWiFi'
    ) {
      console.log('No AP, cleaning creds');
      store.commit('cleanEncryptedCredentialsWrongSsid');
      return;
    }
    // ip obtained - all good
    if (
      lightStatus === LightStatus.IPObtained &&
      pairingStatus.type !== 'creatingDeviceInCloud' &&
      pairingStatus.type !== 'createLightInCloudFail' &&
      pairingStatus.type !== 'encryptingCredentialsFail'
    ) {
      store.commit('updatePairingStatus', {
        type: 'creatingDeviceInCloud',
      });
      window.setTimeout(() => {
        if (
          store.state.pairingStatus.type ===
          'creatingDeviceInCloud' &&
          !store.state.pairingStatus.hintTimeoutReached

        ) {
          console.log('Timeout reached when creating the light in the cloud. Showing hint');
          store.commit('updatePairingStatus', {
            type: 'creatingDeviceInCloud',
            hintTimeoutReached: true
          });
        }
      }, 20000);

      try {
        // it could be the case that phone connected to the wrong AP
        // and thus will not be able to confirm light creation
        // to handle it we can request the system to connect
        await requestToConnectToMainAPIfNeeded(5000);
        // turned out that the iOS sometimes is unwilling to connect to the
        // main SSID and instead chooses another one. We can proceed with
        // light creation as long as current SSID is not the light one
        await waitForDisconnectionFromTheLightSSID(store.state.lightSSID!);
        console.log('Starting light creation');
        const lightCreationResult = await createLightInCloud(
          store.state.registrationData!.homeId,
          store.state.registrationData!.roomId,
          'Light',
          store.state.lightMacAddress!,
          store.state.lightIp || '192.168.56.1',
          store.state.registrationData!.env,
          store.state.registrationData!.region,
          store.state.registrationData!.accessToken,
          store.state.registrationData!.thirdParty
        );
        console.log(
          `Light creation result ${JSON.stringify(lightCreationResult)}`,
        );

        store.commit('updatePairingStatus', { type: 'finishingPairing' });
        await requestToConnectToMainAPIfNeeded(5000);
        // sometimes the phone gets connected to the wrong AP
        // And cannot confirm the light creation via UDP
        // if we cannot get the reply in time -> show the hint
        try {
          window.setTimeout(() => {
            if (
              store.state.pairingStatus.type ===
              'finishingPairing' &&
              !store.state.pairingStatus.hintTimeoutReached
            ) {
              console.log('Timeout reached when confirming light creation via UDP. Showing hint');
              store.commit('updatePairingStatus', {
                type: 'finishingPairing',
                hintTimeoutReached: true
              });
            }
          }, 25000);
          await confirmLightCreation(store.state.lightIp);
        } catch { }
        console.log('Finishing pairing');
        if (store.state.registrationData != undefined && store.state.registrationData.password) {
          (<any>window).newWifiCreds = {
            ssid: store.state.registrationData.ssid,
            password: store.state.registrationData.password,
          };
        }
        (<any>window).closeReason = WindowCloseReason.SuccessfullyPaired;
        window.setTimeout(() => window.close(), 3000);
      } catch {
        console.log('Creating light in cloud timeout');
        store.commit('updatePairingStatus', { type: 'createLightInCloudFail' });
      }
    }
  },
);

async function requestToConnectToMainAPIfNeeded(
  timeout: number | undefined = undefined,
) {
  if (store.state.registrationData != undefined && store.state.currentSSID !== store.state.registrationData.ssid) {
    await waitForConnectionToTheSSID(store.state.registrationData.ssid, timeout);
    if (store.state.currentSSID !== store.state.registrationData.ssid) {
      console.log('Not connected to the initial AP -> requesting connection');
      (<any>window).AppQueue.push({
        type: 'connectToTheInitialAP',
        data: {
          ssid: store.state.registrationData.ssid,
          password: store.state.registrationData.password,
        },
      });
    }
  }
}

function requestToConnectToLightAPIfNeeded() {
  if (
    store.state.pairingStatus.type === 'waitingDeviceToConnectToTheWiFi' &&
    store.state.lightStatus !== LightStatus.WrongPassword &&
    store.state.lightStatus !== LightStatus.APNotFound &&
    store.state.lightStatus !== LightStatus.ConnectionFailed &&
    store.state.lightSSID !== store.state.currentSSID &&
    store.state.lightSSID !== ''
  ) {
    console.log('Not connected to the light AP -> requesting connection');
    (<any>window).AppQueue.push({
      type: 'connectToTheLightSSID',
      data: {
        ssid: store.state.lightSSID,
      },
    });
  }
}

function waitForConnectionToTheSSID(
  ssid: string,
  timeout: number | undefined = undefined,
): Promise<void> {
  return new Promise<void>(resolve => {
    const timeStart = new Date().getTime();
    const token = window.setInterval(() => {
      const now = new Date().getTime();
      console.log(
        `Waiting for connection to ${ssid}, current SSID ${store.state.currentSSID}`,
      );
      if (
        store.state.currentSSID === ssid ||
        now - timeStart > (timeout || Number.MAX_VALUE)
      ) {
        window.clearInterval(token);
        resolve();
      }
    }, 1000);
  });
}

function waitForDisconnectionFromTheLightSSID(
  ssid: string,
  timeout: number | undefined = undefined,
): Promise<void> {
  let numberOfRetries = 0;
  return new Promise<void>(resolve => {
    const timeStart = new Date().getTime();
    const token = window.setInterval(() => {
      if (numberOfRetries < 5) {
        numberOfRetries++;
        window.clearInterval(token);
        resolve();
        return;
      } else {
        const now = new Date().getTime();
        console.log(`Waiting for disconnection from ${ssid}`);
        if (
          store.state.currentSSID !== ssid ||
          now - timeStart > (timeout || Number.MAX_VALUE)
        ) {
          window.clearInterval(token);
          resolve();
        }
      }
    }, 1000);
  });
}

function notifyAppReachedConnectingState() {
  if (store.state.pairingStatus.type === 'connectingToTheLight' && router.currentRoute.name === 'Pairing') {
    // notify the app that the credentials are ready
    // so Android can present the popup selecting the SSID
    console.log('Notifying about reaching Connecting state');
    (<any>window).AppQueue.push({
      type: 'waitingForInitialConnectionToLightSSID',
      data: {},
    });
  }
}

// Connect error monitoring service
initSentry(SENTRY_VERSION);

router.afterEach((to, from) => {
  console.log(`Route changed to ${to.name} from ${from.name}`);
  notifyAppReachedConnectingState();
});
///////////////// Vue and app interaction
/* eslint-disable no-new */
new Vue({
  store,
  el: '#app',
  router,
  components: {
    App,
  },
  template: '<App/>',
});

(<any>window).injectWiZData = injectWiZData;

(<any>window).WiZQueue = (<any>window).WiZQueue || [];
(<any>window).AppQueue = (<any>window).AppQueue || [];

// Interface for mobile app to inject pairing params
function injectWiZData(dt: {
  accessToken: string;
  homeId: number;
  roomId: number;
  ssid: string;
  password: string;
  env: string;
  region: string;
  deviceType: DeviceType | undefined;
  isShortcut: boolean | undefined;
  // hotfixed for the AP pairing bug happened in the Android app
  // when the instructions page was missing
  isShortCut: boolean | undefined;
  thirdParty: string | undefined;
}) {
  // wiz data should be injected only once
  if (store.state.registrationData != undefined) {
    return;
  }
  const shortcutIsNotSet = store.state.isShortcut == undefined;
  store.commit('injectWiZData', dt);
  if (shortcutIsNotSet) {
    if (dt.isShortcut || dt.isShortCut) {
      router.replace('Instructions');
    } else {
      router.replace('Pairing');
    }
  }
  store.commit('updatePairingStatus', {
    type: 'encryptingCredentials',
  });
}

// Mobile app will add commands to the WiZQueue and web will periodically check what's inside
function parseCommandsInQueue() {
  const commandsInQueue: Array<{ type: string; data: any }> = _.concat(
    [],
    (<any>window).WiZQueue,
  );
  (<any>window).WiZQueue = [];

  _.forEach(commandsInQueue, command => {
    if (command.type === 'setWiZData') {
      injectWiZData(command.data);
    } else if (command.type === 'SSIDUpdated') {
      const ssid = command.data.ssid;
      store.commit('currentSSIDChanged', ssid);
      if (
        (ssid.indexOf('WiZConfig') !== -1 && store.state.lightSSID === '') ||
        ssid.trim() === store.state.lightSSID.trim()
      ) {
        store.commit('lightSSIDDiscovered', ssid);
      }
    }
  });
}
parseCommandsInQueue();
setInterval(() => {
  parseCommandsInQueue();
}, 1000);



// WEBPACK FOOTER //
// ./src/main.ts